import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import Tabs from "../../components/Tabs";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listStudents, createStudent } from "../../api/studentApi";
import { getInstituteProfile } from "../../api/instituteApi";
import { sendSmsToStudents } from "../../api/smsApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");
const groupApi = createLookupApi("groups");
const categoryApi = createLookupApi("student-categories");

const GENDERS = ["Male", "Female"];
const RELIGIONS = ["Islam", "Hinduism", "Buddist", "Christian", "Other"];

const emptyRow = (roll, customMode) => ({
  key: Math.random(),
  selected: true,
  studentRoll: roll != null ? String(roll) : "",
  customStudentId: "",
  studentName: "",
  registrationId: "",
  studentGender: "",
  studentReligion: "",
  fatherName: "",
  motherName: "",
  guardianMobile: "",
  studentDob: "",
  mailingAddress: "",
});

/**
 * Direct port of digicampusbd's two real bulk student-registration pages —
 * `student-multiple-registration` ("Student Registration With Auto Id") and
 * `student-multiple-registration-custom` ("Custom Id Registration") — one
 * spreadsheet-style grid for registering several students in a single save,
 * distinct from (and previously missing entirely next to) both the
 * single-student `StudentFormPage` and the Excel-upload bulk pages
 * (`StudentExcelRegistrationWith[out]IdPage`).
 *
 * A prior migration phase had pointed both urls at `StudentFormPage` — a
 * full one-student-at-a-time detailed form with no multi-row capability at
 * all, silently dropping this real, faster "type several rows, save once"
 * workflow. Confirmed the real shape by running digicampusbd itself live
 * (same `ms_db`) rather than reading templates alone: Auto Id shows
 * Select/Roll/Name/[Registration No]/Gender/Religion/Father Name/Mother
 * Name/Mobile No/[Date of Birth]/[Address]/Add-Del (the bracketed three are
 * toggle-able columns, all hidden by default); Custom Id additionally always
 * shows a Student Id column (no auto-id generation) and never has the
 * Date-of-Birth/Address toggle columns at all — confirmed against the old
 * app's own real `student-multiple-registration-custom.html`, which simply
 * doesn't define them.
 *
 * **Zero new backend needed.** digicampusbd's own two save endpoints for
 * these pages (`/student/registration/multiple/save` for Auto Id,
 * `/student/registration/excel/with/student-id` for Custom Id — the exact
 * same endpoint the "Excel Custom ID" upload page already posts to) both
 * ultimately do the same per-student create digicampusbd's single-
 * registration page does. This port reuses the already-existing
 * `POST /api/students` (`createStudent`) once per selected row — the same
 * proven, zero-new-backend pattern `StudentExcelRegistrationWithIdPage`
 * already established for the sibling Excel flow — rather than adding a new
 * bulk endpoint: `createStudent` already auto-generates `customStudentId`
 * when blank (Auto Id) and already accepts one when supplied (Custom Id).
 */
export default function StudentMultipleRegistrationPage() {
  const { pageUrl } = useParams();
  const history = useHistory();
  const customMode = pageUrl === "student-multiple-registration-custom";

  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");

  const [showRegNo, setShowRegNo] = useState(false);
  const [showDob, setShowDob] = useState(false);
  const [showAddress, setShowAddress] = useState(false);

  const [rows, setRows] = useState([emptyRow(1, customMode)]);
  const [sendSms, setSendSms] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigurationId(String(configs[0].id));
    });
    groupApi.list().then(setGroups);
    categoryApi.list().then(setCategories);
  }, []);

  // digicampusbd's own classconfigid-change handler: suggest the next roll (max existing + 1) on the last row.
  useEffect(() => {
    if (!classConfigurationId || !academicYear) return;
    listStudents(academicYear, classConfigurationId).then((list) => {
      const maxRoll = list.reduce((max, s) => Math.max(max, s.studentRoll || 0), 0);
      setRows((prev) => {
        if (prev.length !== 1) return prev; // only auto-fill a still-fresh single-row table, matching the old app
        const [only] = prev;
        if (only.studentName || only.fatherName || only.motherName) return prev;
        return [{ ...only, studentRoll: String(maxRoll + 1) }];
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classConfigurationId, academicYear]);

  const setCell = (key, field, value) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));

  const addRow = () => {
    setRows((prev) => {
      const lastRoll = parseInt(prev[prev.length - 1]?.studentRoll, 10);
      const nextRoll = Number.isNaN(lastRoll) ? "" : lastRoll + 1;
      return [...prev, emptyRow(nextRoll, customMode)];
    });
  };
  const removeRow = (key) => setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));

  const missingFilters = !academicYear || !classConfigurationId || !groupId || !categoryId;

  const handleSave = async () => {
    setError("");
    setResult(null);
    if (missingFilters) {
      setError("Please select academic year, section, group, and category.");
      return;
    }
    const selectedRows = rows.filter((r) => r.selected);
    for (const r of selectedRows) {
      if (!r.studentRoll) {
        setError("Roll is required for every selected row.");
        return;
      }
      if (!r.studentGender) {
        setError("Gender is required for every selected row.");
        return;
      }
      if (!r.fatherName) {
        setError("Father name is required for every selected row.");
        return;
      }
      if (!r.motherName) {
        setError("Mother name is required for every selected row.");
        return;
      }
      if (!r.guardianMobile) {
        setError("Mobile Number is required for every selected row.");
        return;
      }
    }
    if (selectedRows.length === 0) {
      setError("Please select at least one row to save.");
      return;
    }

    setSaving(true);
    let success = 0;
    const failures = [];
    const created = [];
    for (const r of selectedRows) {
      try {
        const student = await createStudent({
          studentName: r.studentName,
          studentGender: r.studentGender,
          studentReligion: r.studentReligion,
          fatherName: r.fatherName,
          motherName: r.motherName,
          guardianMobile: r.guardianMobile,
          registrationNo: r.registrationId,
          customStudentId: customMode ? r.customStudentId || undefined : undefined,
          studentDob: !customMode && showDob ? r.studentDob : undefined,
          mailingAddress: !customMode && showAddress ? r.mailingAddress : undefined,
          academicYear,
          classConfigurationId: Number(classConfigurationId),
          groupId: Number(groupId),
          categoryId: Number(categoryId),
          studentRoll: Number(r.studentRoll),
        });
        success++;
        created.push(student);
      } catch (err) {
        failures.push(`Roll ${r.studentRoll} (${r.studentName || "unnamed"}): ${(err.response && err.response.data && err.response.data.message) || "failed"}`);
      }
    }
    setResult({ success, total: selectedRows.length, failures });

    // Direct port of digicampusbd's own post-registration SMS text (StudentRegistrationService) — sent one
    // call per student since each message embeds that student's own name and newly-assigned custom ID.
    if (sendSms && created.length > 0) {
      try {
        const institute = await getInstituteProfile();
        await Promise.all(
          created.map((student) =>
            sendSmsToStudents({
              identificationIds: [student.identificationId],
              message: `Congratulations ${student.studentName}. Your Registration is completed at ${institute.instituteName}.Your Student ID= ${student.customStudentId}, EIIN NO= ${institute.eiinNo} and Institute ID= ${institute.instituteId}`,
            })
          )
        );
      } catch (err) {
        // Best-effort, matching the old app's own silent balance/gateway-failure swallow — registration itself already succeeded.
      }
    }
    setSaving(false);

    if (success > 0) {
      // Matches digicampusbd's own post-save reset: back to one fresh row, next roll suggested from the last saved roll.
      const lastRoll = parseInt(selectedRows[selectedRows.length - 1].studentRoll, 10);
      setRows([emptyRow(Number.isNaN(lastRoll) ? "" : lastRoll + 1, customMode)]);
      setSendSms(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>{customMode ? "Custom Id Registration" : "Student Registration With Auto Id"}</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      {result && (
        <Alert type={result.failures.length > 0 ? "error" : "success"} onDismiss={() => setResult(null)}>
          Registered {result.success} of {result.total} selected student(s).
          {result.failures.length > 0 && (
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {result.failures.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          )}
        </Alert>
      )}

      <Tabs
        tabs={[
          { key: "auto", label: "Auto ID" },
          { key: "custom", label: "Custom ID" },
        ]}
        active={customMode ? "custom" : "auto"}
        onChange={(key) =>
          history.push(key === "custom" ? "/student-multiple-registration-custom" : "/student-multiple-registration")
        }
      />

      <div className="attendance-filters">
        <label>
          Select Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Section
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Group
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            <option value="">Select Group</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Student Category
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">Select Student Category</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {!customMode && (
        <div className="attendance-filters" style={{ alignItems: "center" }}>
          <span style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
            Columns: (<span style={{ color: "var(--color-danger)" }}>*</span>)
          </span>
          <label style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={showRegNo} onChange={(e) => setShowRegNo(e.target.checked)} />
            Registration No.
          </label>
          <label style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={showDob} onChange={(e) => setShowDob(e.target.checked)} />
            Date of Birth
          </label>
          <label style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={showAddress} onChange={(e) => setShowAddress(e.target.checked)} />
            Address
          </label>
        </div>
      )}

      {missingFilters ? (
        <p style={{ color: "var(--color-text-muted)" }}>Select academic year, section, group, and category to begin.</p>
      ) : (
        <>
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Select</th>
                  <th>Roll</th>
                  {customMode && <th>Student Id</th>}
                  <th>Name</th>
                  {(customMode || showRegNo) && <th>Registration No</th>}
                  <th>Gender</th>
                  <th>Religion</th>
                  <th>Father Name</th>
                  <th>Mother Name</th>
                  <th>Mobile No.</th>
                  {!customMode && showDob && <th>Date of Birth</th>}
                  {!customMode && showAddress && <th>Address</th>}
                  <th>Add/Del</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key}>
                    <td>
                      <input
                        type="checkbox"
                        checked={r.selected}
                        onChange={(e) => setCell(r.key, "selected", e.target.checked)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        style={{ width: 70 }}
                        value={r.studentRoll}
                        onChange={(e) => setCell(r.key, "studentRoll", e.target.value)}
                      />
                    </td>
                    {customMode && (
                      <td>
                        <input value={r.customStudentId} onChange={(e) => setCell(r.key, "customStudentId", e.target.value)} />
                      </td>
                    )}
                    <td>
                      <input value={r.studentName} onChange={(e) => setCell(r.key, "studentName", e.target.value)} />
                    </td>
                    {(customMode || showRegNo) && (
                      <td>
                        <input value={r.registrationId} onChange={(e) => setCell(r.key, "registrationId", e.target.value)} />
                      </td>
                    )}
                    <td>
                      <select value={r.studentGender} onChange={(e) => setCell(r.key, "studentGender", e.target.value)}>
                        <option value="" disabled hidden>
                          Select Gender
                        </option>
                        {GENDERS.map((g) => (
                          <option key={g} value={g}>
                            {g}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select value={r.studentReligion} onChange={(e) => setCell(r.key, "studentReligion", e.target.value)}>
                        <option value="" disabled hidden>
                          Select Religion
                        </option>
                        {RELIGIONS.map((rel) => (
                          <option key={rel} value={rel}>
                            {rel}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input value={r.fatherName} onChange={(e) => setCell(r.key, "fatherName", e.target.value)} />
                    </td>
                    <td>
                      <input value={r.motherName} onChange={(e) => setCell(r.key, "motherName", e.target.value)} />
                    </td>
                    <td>
                      <input
                        maxLength={11}
                        value={r.guardianMobile}
                        onChange={(e) => setCell(r.key, "guardianMobile", e.target.value)}
                      />
                    </td>
                    {!customMode && showDob && (
                      <td>
                        <input type="date" value={r.studentDob} onChange={(e) => setCell(r.key, "studentDob", e.target.value)} />
                      </td>
                    )}
                    {!customMode && showAddress && (
                      <td>
                        <input value={r.mailingAddress} onChange={(e) => setCell(r.key, "mailingAddress", e.target.value)} />
                      </td>
                    )}
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button type="button" className="btn-link" onClick={addRow} title="Add row">
                        +
                      </button>{" "}
                      {rows.length > 1 && (
                        <button type="button" className="btn-link danger" onClick={() => removeRow(r.key)} title="Remove row">
                          Del
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="form-actions" style={{ justifyContent: "flex-end", alignItems: "center" }}>
            <label style={{ marginRight: 12 }}>
              <input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} /> Send Sms
            </label>
            <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </>
      )}
    </MainLayout>
  );
}
